// ClaimsFilter.js
import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TablePagination from '@mui/material/TablePagination';
import { Select, MenuItem } from '@mui/material';



const normalizeStatusOptions = (availableStatuses = []) => {
    return availableStatuses.map((s) => {
        if (typeof s === 'string') return { label: String(s), value: s };
        const value = s?.value ?? s?.description ?? s?.label ?? String(s ?? '');
        const label = s?.label ?? String(value);
        return { label, value };
    });
};

const getSelectedMultiValues = (selectedStatuses = [], options = []) => {
    const selectedValueSet = new Set(
          selectedStatuses.map((s) =>
                String(typeof s === 'object' ? (s.value ?? s.description ?? s.label) : s)
              )
        );
    return options.filter((o) => selectedValueSet.has(String(o.value)));
};

const ClaimsFilter = (props) => {
    const {
        totalCount,
        availableStatuses = [],
        selectedStatuses = [],
        fromFullDate,
        toFullDate,
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions = [10, 25, 50, 100],
        setFromDate,
        setToDate,
        setStatus,
        setNumber,
        fetchClaims,
        resetFilters,
        saveChangesClaimApproval,
        listView,
        localized = {},
        numberFilter = '',
    } = props;


    

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const PERIODS = React.useMemo(
        () => [
            { value: 30, label: localized.Last30Days },
            { value: 90, label: localized.Last90Days },
            { value: 365, label: localized.LastYear },
            { value: 730, label: localized.Last2Years },
        ],
        [localized.Last30Days, localized.Last90Days, localized.LastYear, localized.Last2Years]
    );

    const [periodDays, setPeriodDays] = React.useState(() => {
        const from = new Date(fromFullDate);
        const to = new Date(toFullDate ?? Date.now());
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
            const diff = Math.max(1, Math.round((to - from) / (24 * 60 * 60 * 1000)));
            const closest = PERIODS.reduce(
                (prev, curr) => (Math.abs(curr.value - diff) < Math.abs(prev.value - diff) ? curr : prev),
                PERIODS[0]
            );
            return closest.value;
        }
        return 30;
    });

    const statusOptions = React.useMemo(
        () => normalizeStatusOptions(availableStatuses),
        [availableStatuses]
    );

    const statusDisabled = !Number.isFinite(totalCount) || totalCount <= 0 || statusOptions.length === 0;

    const statusValue = React.useMemo(
        () => getSelectedMultiValues(selectedStatuses, statusOptions),
        [selectedStatuses, statusOptions]
    );

    React.useEffect(() => {
        if (statusDisabled && statusValue.length > 0) {
            handleStatusChange([]);
        }
    }, [statusDisabled]);

    const handleEnter = (e) => {
        if (e.key === 'Enter') fetchClaims?.();
    };

    const resetToFirstPage = () => {
        setPaginationModel?.((pm) => ({ ...(pm ?? {}), page: 0 }));
    }

    const handleStatusChange = (arr) => {
              const single = Array.isArray(arr) ? arr.slice(-1) : (arr ? [arr] : []);
          setStatus?.(single);
          resetToFirstPage();
        };


    const handleNumberChange = (e) => {
        setNumber?.(e.target.value ?? '');
        resetToFirstPage();
    };

    const onChangePeriod = (e) => {
        const days = Number(e.target.value) || 30;
        setPeriodDays(days);
        const now = new Date();
        const from = new Date();
        from.setDate(now.getDate() - days);
        setFromDate?.(from);
        setToDate?.(now);
        resetToFirstPage();
    };

    const PaginationRow = () => {
        const total = Number.isFinite(totalCount) ? totalCount : 0;
        const page = Math.max(0, paginationModel?.page ?? 0); // 0-based
        const pageSize = paginationModel?.pageSize ?? 25;
        const options = rowsPerPageOptions?.length ? rowsPerPageOptions : [10, 25, 50, 100];

        const handleChangePage = (_evt, newPage) => {
            setPaginationModel?.({ ...(paginationModel ?? {}), page: newPage });
        };
        const handleChangeRowsPerPage = (evt) => {
            const newSize = parseInt(evt.target.value, 10);
            setPaginationModel?.({ ...(paginationModel ?? {}), pageSize: newSize, page: 0 });
        };

        return (
            <div className="row" style={{ marginTop: 8 }}>
                <div className="col-12 d-flex justify-content-end">
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={pageSize}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={options}
                        labelRowsPerPage={localized.RowsPerPage}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${localized.Of} ${count}`}
                        sx={{
                            color: 'white',
                            '& .MuiTablePagination-toolbar': { minHeight: 36, px: 0.5 },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: 'white' },
                            '& .MuiTablePagination-actions button': { color: 'white' },
                            '& .MuiInputBase-input': { color: 'white' },
                            '& .MuiSvgIcon-root': { color: 'white' },
                        }}
                    />
                </div>
            </div>
        );
    };
    const ALL = '__ALL__'

    const renderDesktop = () => (
        <div>
            <div
                className="container-fluid"
                style={{
                    background: '#172b42',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    paddingBottom: '15px',
                    width: '100%',
                    marginLeft: '0px',
                }}
            >
                <div className="row gy-2">
                    <div className="col-xl-2 col-lg-2 d-flex">
                        <div style={{ maxWidth: '100%', minWidth: '100%' }}>
                            <div className="label" style={{ color: 'white' }}>
                                {localized.ClaimStatus}
                            </div>
                            <div className="input-wrapper">
                                <select
                                    aria-label={localized.ClaimStatus}
                                    style={{
                                        width: '100%',
                                        height: '3.2rem',
                                        borderRadius: 6,
                                        padding: '0 10px',
                                        backgroundColor: statusDisabled ? '#f2f4f7' : undefined,
                                        color: statusDisabled ? '#6b7280' : undefined,

                                    }}
                                    disabled={statusDisabled}
                                    value={statusValue[0]?.value ?? ALL}
                                    onChange={(e) => {
                                        if (statusDisabled) return;
                                        const v = e.target.value;
                                        if (v === ALL) return handleStatusChange([]);
                                        const opt = statusOptions.find(o => String(o.value) === String(v));
                                        handleStatusChange(opt ? [opt] : []);
                                      }}
                                >
                                    <option value={ALL}><em>{statusDisabled ? localized.NoClaimsFound : localized.AllClaimStatuses}</em></option>
                                  {statusOptions.map(o => (
                                        <option key={o.value} value={String(o.value)}>{o.label}</option>
                                      ))}
                                </select>

                            </div>
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-4">
                        <div className="label" style={{ color: 'white' }}>
                            {localized.DateRange}
                        </div>
                        <div className="input-wrapper">
                            <select
                                aria-label={localized.DateRange}
                                value={periodDays}
                                onChange={onChangePeriod}
                                style={{
                                    width: '100%',
                                    height: '3.2rem',
                                    borderRadius: 6,
                                    padding: '0 10px',
                                }}
                            >
                                {PERIODS.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-lg-3">
                        <div className="label" style={{ color: 'white' }}>
                            {localized.SearchForClaims}
                        </div>
                        <div className="form-group orderSearch">
                            <input
                                data-cy="claims-search"
                                type="text"
                                placeholder={localized.ClaimFilterSearch}
                                onChange={handleNumberChange}
                                onKeyPress={handleEnter}
                                value={numberFilter}
                            />
                        </div>
                    </div>
                    <div className="col-xl-5 col-lg-4">
                        <label></label>
                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                            <div>
                                <div className="button-wrapper float-end">
                                    <button
                                        data-cy="claims-submit-search"
                                        className="button search-button button-alt"
                                        style={{ height: '3.2rem' }}
                                        onClick={fetchClaims}
                                    >
                                        {localized.Search}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="button-wrapper float-end">
                                    <button
                                        className="button search-button button-alt"
                                        style={{ height: '3.2rem' }}
                                        onClick={() => {
                                            resetFilters?.();
                                            setPeriodDays(30);
                                        }}
                                    >
                                        {localized.ResetButtonText}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="button-wrapper float-end" style={{ marginRight: '10px' }}>
                                    <button
                                        className="button search-button button-alt"
                                        style={{ height: '3.2rem' }}
                                        onClick={() => {
                                            saveChangesClaimApproval?.();
                                        }}
                                    >
                                        {localized.SaveChangesButtonText}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Show pagination row under filter when used above a list view */}
                {listView && <PaginationRow />}
            </div>
        </div>
    );

    const renderMobile = () => (
        <div>
            <div
                className="container-fluid"
                style={{
                    background: '#172b42',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    padding: '12px',
                    width: '100%',
                    marginLeft: 0,
                }}
            >
                <div
                    className="d-flex"
                    style={{ gap: 8, alignItems: 'flex-start', overflow: 'visible', flexWrap: 'nowrap' }}
                >
                    <div style={{ flex: '0 0 60%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: 'white', marginBottom: 6 }}>
                            {localized.Search}
                        </div>
                        <div className="form-group orderSearch">
                            <input
                                data-cy="claims-search"
                                type="text"
                                placeholder={localized.ClaimFilterSearch}
                                onChange={handleNumberChange}
                                onKeyPress={handleEnter}
                                value={numberFilter}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <div style={{ flex: '0 0 40%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: 'white', marginBottom: 6 }}>
                            {localized.ClaimStatus}
                        </div>
                        <select
                            aria-label={localized.ClaimStatus}
                            style={{
                                width: '100%',
                                height: '3.2rem',
                                borderRadius: 6,
                                padding: '0 10px',
                            }}
                            value={statusValue[0]?.value ?? ALL}
                            onChange={(e) => {
                                if (statusDisabled) return;
                                const v = e.target.value;
                                if (v === ALL) return handleStatusChange([]);
                                const opt = statusOptions.find(o => String(o.value) === String(v));
                                handleStatusChange(opt ? [opt] : []);
                            }}
                        >
                            <option value={ALL}><em>{localized.AllClaimStatuses}</em></option>
                            {statusOptions.map(o => (
                                <option key={o.value} value={String(o.value)}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div
                    className="d-flex mt-2"
                    style={{ gap: 8, alignItems: 'flex-start', overflow: 'visible', flexWrap: 'nowrap' }}
                >
                    <div style={{ flex: '1 1 100%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: 'white', marginBottom: 6 }}>
                            {localized.DateRange}
                        </div>
                        <select
                            aria-label={localized.DateRange}
                            value={periodDays}
                            onChange={onChangePeriod}
                            style={{
                                width: '100%',
                                height: '3.2rem',
                                borderRadius: 6,
                                padding: '0 10px',
                            }}
                        >
                            {PERIODS.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-3 d-flex" style={{ gap: 8 }}>
                    <button
                        data-cy="claims-submit-search"
                        className="button search-button button-alt"
                        style={{ height: '3.2rem', flex: 1 }}
                        onClick={fetchClaims}
                    >
                        {localized.Search}
                    </button>
                    <button
                        className="button search-button button-alt"
                        style={{ height: '3.2rem', flex: 1 }}
                        onClick={() => {
                            resetFilters?.();
                            setPeriodDays(30);
                        }}
                    >
                        {localized.ResetButtonText}
                    </button>
                    <button
                        className="button search-button button-alt"
                        style={{ height: '3.2rem', flex: 1 }}
                        onClick={() => {
                            saveChangesClaimApproval?.();
                        }}
                    >
                        {localized.SaveChangesButtonText}
                    </button>
                </div>
                <PaginationRow />
            </div>
        </div>
    );

    return isMobile ? renderMobile() : renderDesktop();
};

ClaimsFilter.propTypes = {
    totalCount: PropTypes.number,
    availableStatuses: PropTypes.array,                
    selectedStatuses: PropTypes.array,         
    fromFullDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
        PropTypes.number,
    ]),
    toFullDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
        PropTypes.number,
    ]),
    paginationModel: PropTypes.shape({
        page: PropTypes.number,
        pageSize: PropTypes.number,
    }),
    setPaginationModel: PropTypes.func,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    setFromDate: PropTypes.func,
    setToDate: PropTypes.func,
    setStatus: PropTypes.func,
    setNumber: PropTypes.func,
    fetchClaims: PropTypes.func,
    resetFilters: PropTypes.func,
    saveChangesClaimApproval: PropTypes.func,
    listView: PropTypes.bool,
    localized: PropTypes.object,
    numberFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ClaimsFilter;